import React, { PropsWithChildren, useCallback } from 'react';
import TreeConfigContext from 'App/TreeConfigContext';
import * as uuid from 'uuid';
import BackendQueryEngine from 'backend/BackendQueryEngine';
import { TreeGraph } from 'types/backend/response/TreeGraph';

interface Props {
    topK: number;
    nWords: number;
}

const TreeConfigContextProvider = ({ children, topK, nWords }: PropsWithChildren<Props>) => {
    const [targetTreeId, setTargetTreeId] = React.useState<string | null>(null);
    const [ontologyNodeContents, setOntologyNodeContents] = React.useState<string[]>([]);

    // This is an internal dummy state that is used to trigger a re-load when the tree version changes.
    const [treeVersion, setTreeVersion] = React.useState<string>(uuid.v4());

    const [optionalTreeGraphs, setTreeGraphs] = React.useState<TreeGraph[]>();

    // Define methods to add or remove ontology node contents. Make sure they are unique.
    const addOntologyNodeContent = useCallback((content: string) => {
        setOntologyNodeContents((prevContents) => [...new Set([...prevContents, content])]);
    }, []);
    const removeOntologyNodeContent = useCallback((content: string) => {
        setOntologyNodeContents((prevContents) => prevContents.filter((c) => c !== content));
    }, []);

    const selectTree = useCallback((treeId: string) => {
        // Load persisted ontology node contents for this tree first,
        // then set the targetTreeId so the first load uses the correct contents.
        BackendQueryEngine.treeGetOntologyNodeContents(treeId)
            .then((contents) => {
                setOntologyNodeContents(contents ?? []);
                setTargetTreeId(treeId);
            })
            .catch((e) => {
                console.warn(e);
                // Even if loading fails, proceed with empty contents.
                setOntologyNodeContents([]);
                setTargetTreeId(treeId);
            });
    }, []);

    const reloadTree = useCallback(() => {
        setTreeVersion(uuid.v4());
    }, []);

    // Persist ontologyNodeContents for the selected tree whenever they change.
    React.useEffect(() => {
        if (targetTreeId) {
            BackendQueryEngine.treeSetOntologyNodeContents(targetTreeId, ontologyNodeContents).catch((e) =>
                console.warn(e)
            );
        }
    }, [ontologyNodeContents, targetTreeId]);

    // If one of the dependencies changes, update the tree.
    React.useEffect(() => {
        if (targetTreeId) {
            if (ontologyNodeContents.length === 0) {
                BackendQueryEngine.treeReplacePredictAndGet(targetTreeId, {
                    ontologyNodeContent: null,
                    topK,
                    nWords,
                }).then((treeGraph) => setTreeGraphs([treeGraph]));
            } else {
                Promise.all(
                    ontologyNodeContents.map((ontologyNodeContent) => {
                        return BackendQueryEngine.treeReplacePredictAndGet(targetTreeId, {
                            ontologyNodeContent,
                            topK,
                            nWords,
                        });
                    })
                ).then((treeGraphs) => setTreeGraphs(treeGraphs));
            }
        }
    }, [nWords, ontologyNodeContents, targetTreeId, topK, treeVersion]);

    return (
        <TreeConfigContext.Provider
            value={{
                selectTree,
                reloadTree,
                optionalTreeGraphs,
                ontologyNodeContents,
                addOntologyNodeContent,
                removeOntologyNodeContent,
            }}
        >
            {children}
        </TreeConfigContext.Provider>
    );
};

export default TreeConfigContextProvider;
